import { useCallback, useContext } from "react";
import { QueryClient, QueryClientContext, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import customInstance from "@/api/client";
import type { QueryShape } from "../meHooks";

export interface MeSession {
  uuid: string;
  status: string;
  startUtc: string;
  endUtc: string;
  tzid: string;
  startLocal: string;
  endLocal: string;
  joinDeeplink: string;
  joinUrl: string | null;
  /** The two instants that BOUND the join control, derived server-side from the same helpers
   *  the guard uses (`join_window_before_start` / `join_window_after_end`).
   *
   *  🔴 NEVER RE-DERIVE THESE FROM `startUtc` / `endUtc` PLUS A LOCAL CONSTANT. That is what
   *  this module's `SESSION_JOIN_GATE_MS = 5 * 60 * 1000` did until 2026-09-25: it could not
   *  follow `JOIN_WINDOW_BEFORE_START_MINUTES`, so raising the env var moved the guard and the
   *  session email's promise and left the button saying five. And closing on `endUtc` dropped
   *  the Join affordance the instant a session passed its SCHEDULED end — while the therapist
   *  was still in the call, which is the bug that produced these fields. */
  joinOpensAt: string;
  joinClosesAt: string;
  /** True when this occurrence came from the couple's standing (weekly) slot — the
   *  server's own linkage, not something inferred from the instant. */
  fromStandingSlot: boolean;
  /** WHICH day the couple's weekly slot falls on, `Mon=0 … Sun=6`, already resolved into
   *  the same timezone as `startLocal`. `null` for an ad-hoc booking — and for a standing
   *  occurrence whose slot the server could not read.
   *
   *  Never derive this from `startLocal`: a rescheduled occurrence keeps `fromStandingSlot`
   *  while its own instant leaves the slot's weekday, so the instant would name the day the
   *  session MOVED TO as the day the couple recurs on. Only the slot knows the rule. */
  standingSlotWeekday: number | null;
  /** Where the session happens, as the server's code (`video_call` or `in_person`). */
  location: string;
  /** The therapist's office address. Null for video and when none is on file. */
  locationAddress: string | null;
}

// `from_standing_slot` and `standing_slot_weekday` ride on `CoachedSessionOut`, so
// BOTH session reads carry them — the upcoming list and the single-session detail
// (`getSession`). What does not carry them is the PAST wire: `/me/sessions/past`
// returns `PastSessionOut`, a different shape without either field. Omitted here
// rather than typed optional so nothing can read a field the past wire never sends.
export interface PastSession
  extends Omit<
    MeSession,
    | "joinDeeplink"
    | "joinUrl"
    | "joinOpensAt"
    | "joinClosesAt"
    | "fromStandingSlot"
    | "standingSlotWeekday"
    | "location"
    | "locationAddress"
  > {
  sessionNumber: number;
  hasFeedback: boolean;
  feedbackWindowOpen: boolean;
}

interface WireSession {
  uuid: string;
  status: string;
  start_utc: string;
  end_utc: string;
  tzid: string;
  start_local: string;
  end_local: string;
  join_deeplink: string;
  join_url: string | null;
  // REQUIRED, unlike the optional fields below, and deliberately so: the server always computes
  // both, and there is no honest default a client could substitute. A fallback here would be a
  // re-introduced local constant — the exact thing these fields remove.
  join_opens_at: string;
  join_closes_at: string;
  // OPTIONAL, matching the generated schema (`CoachedSessionOut.from_standing_slot?`):
  // the server field has a default, so the contract permits its absence. Required
  // here would let a stub that omits it typecheck while prod disagrees.
  from_standing_slot?: boolean;
  // Optional AND nullable, matching `CoachedSessionOut.standing_slot_weekday?: number | null`
  // — absent on an older payload, explicitly null whenever the server has no weekday to name.
  standing_slot_weekday?: number | null;
  location?: string;
  location_address?: string | null;
}

interface WirePastSession
  extends Omit<
    WireSession,
    | "join_deeplink"
    | "join_opens_at"
    | "join_closes_at"
    | "from_standing_slot"
    | "standing_slot_weekday"
    | "location"
    | "location_address"
  > {
  session_number: number;
  has_feedback: boolean;
  feedback_window_open: boolean;
}

interface WirePastSessionsPage {
  sessions: WirePastSession[];
  total: number;
  next_before_ts: string | null;
  next_before_uuid: string | null;
}

interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

interface PastCursor {
  beforeTs: string;
  beforeUuid: string;
}

export const ME_UPCOMING_SESSIONS_QUERY_KEY = ["me", "sessions", "upcoming"] as const;
export const ME_PAST_SESSIONS_QUERY_KEY = ["me", "sessions", "past"] as const;

const detachedQueryClient = new QueryClient();

function useQueryClientSafe(): QueryClient {
  return useContext(QueryClientContext) ?? detachedQueryClient;
}

function sessionFromWire(wire: WireSession): MeSession {
  return {
    uuid: wire.uuid,
    status: wire.status,
    startUtc: wire.start_utc,
    endUtc: wire.end_utc,
    tzid: wire.tzid,
    startLocal: wire.start_local,
    endLocal: wire.end_local,
    joinDeeplink: wire.join_deeplink,
    joinUrl: wire.join_url,
    joinOpensAt: wire.join_opens_at,
    joinClosesAt: wire.join_closes_at,
    // Absent means NOT a standing slot — the honest reading of a missing field.
    // Never assert a standing slot the server did not claim.
    fromStandingSlot: wire.from_standing_slot ?? false,
    // Same reading for the weekday: no value means the day is UNKNOWN, and an unknown day
    // is one the UI must not name. Absent and null collapse to the one `null` callers check.
    standingSlotWeekday: wire.standing_slot_weekday ?? null,
    location: wire.location ?? "video_call",
    locationAddress: wire.location_address ?? null,
  };
}

function pastSessionFromWire(wire: WirePastSession): PastSession {
  return {
    uuid: wire.uuid,
    status: wire.status,
    sessionNumber: wire.session_number,
    startUtc: wire.start_utc,
    endUtc: wire.end_utc,
    tzid: wire.tzid,
    startLocal: wire.start_local,
    endLocal: wire.end_local,
    hasFeedback: wire.has_feedback,
    feedbackWindowOpen: wire.feedback_window_open,
  };
}

async function getUpcomingSessions(signal?: AbortSignal): Promise<MeSession[]> {
  const response = await customInstance<ApiResponse<WireSession[]>>("/me/sessions/upcoming", {
    method: "GET",
    signal,
  });
  return response.data.map(sessionFromWire);
}

async function getSession(sessionUuid: string, signal?: AbortSignal): Promise<MeSession> {
  const response = await customInstance<ApiResponse<WireSession>>(`/me/sessions/${sessionUuid}`, {
    method: "GET",
    signal,
  });
  return sessionFromWire(response.data);
}

async function getPastSessions(pageSize: number, cursor: PastCursor | null, signal?: AbortSignal): Promise<{
  sessions: PastSession[];
  total: number;
  nextCursor: PastCursor | null;
}> {
  const params = new URLSearchParams({ limit: String(pageSize) });
  if (cursor) {
    params.set("before_ts", cursor.beforeTs);
    params.set("before_uuid", cursor.beforeUuid);
  }
  const response = await customInstance<ApiResponse<WirePastSessionsPage>>(`/me/sessions/past?${params.toString()}`, {
    method: "GET",
    signal,
  });
  return {
    sessions: response.data.sessions.map(pastSessionFromWire),
    total: response.data.total,
    nextCursor:
      response.data.next_before_ts && response.data.next_before_uuid
        ? { beforeTs: response.data.next_before_ts, beforeUuid: response.data.next_before_uuid }
        : null,
  };
}

/**
 * Invalidate every session-derived read (`["me","sessions",…]` prefix:
 * upcoming, past pages, session details, check-in/feedback state). The hub's
 * ticking clock fires this when a live session crosses its end_utc so the
 * ended session drops out of upcoming and lands in past/feedback without a
 * reload.
 */
export function useInvalidateSessions(): () => void {
  const queryClient = useQueryClientSafe();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["me", "sessions"] });
  }, [queryClient]);
}

export function useUpcomingSessions(opts: { enabled?: boolean } = {}): QueryShape<MeSession[]> {
  const { enabled = true } = opts;
  const queryClient = useQueryClientSafe();
  const query = useQuery(
    {
      queryKey: ME_UPCOMING_SESSIONS_QUERY_KEY,
      enabled,
      queryFn: ({ signal }) => getUpcomingSessions(signal),
    },
    queryClient,
  );
  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}

export function useSession(
  sessionUuid: string | undefined,
  opts: { enabled?: boolean } = {},
): QueryShape<MeSession> & { refetch: () => Promise<unknown> } {
  const { enabled = true } = opts;
  const queryClient = useQueryClientSafe();
  const query = useQuery(
    {
      queryKey: ["me", "sessions", sessionUuid ?? ""] as const,
      enabled: enabled && !!sessionUuid,
      queryFn: ({ signal }) => getSession(sessionUuid as string, signal),
    },
    queryClient,
  );
  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    // Stable identity (react-query guarantee) — safe as an effect dependency.
    // Returns the underlying refetch promise so callers CAN await/handle it;
    // fire-and-forget callers discard it explicitly (void refetch()).
    refetch: query.refetch,
  };
}

export function usePastSessions(opts: { pageSize?: number; enabled?: boolean } = {}) {
  const { pageSize = 20, enabled = true } = opts;
  const queryClient = useQueryClientSafe();
  const query = useInfiniteQuery(
    {
      queryKey: [...ME_PAST_SESSIONS_QUERY_KEY, pageSize] as const,
      enabled,
      initialPageParam: null as PastCursor | null,
      queryFn: ({ pageParam, signal }) => getPastSessions(pageSize, pageParam, signal),
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
    queryClient,
  );
  const pages = query.data?.pages ?? [];
  return {
    sessions: pages.flatMap((page) => page.sessions),
    total: pages[0]?.total ?? 0,
    hasMore: !!query.hasNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    error: query.error instanceof Error ? query.error : null,
  };
}
